import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../context/AuthContext";
import { useAssociationAuth } from "../context/AssociationAuthContext";
import { NO_IMAGE_PLACEHOLDER } from "../utils/placeholderImage";
import { notifyAssoCannot } from "./AssoCannotBuyNotice";
import { HeartIcon, StarIcon, FireIcon } from "./Icons";
import { slugify } from "../utils/slugify";
import { isFreeListing, isBookingListing, bookingBadge, isReserved, formatListingPrice } from "../utils/rental";
import { getTerms } from "../utils/terms";
import { buildListingImageAlt } from "../utils/listingImageAlt";
import { classementSponsorise } from "../utils/classementSponsorise";
import TAXONOMY from "../constants/taxonomy.generated";

const _eurCompact = (v) => {
  const entier = Math.abs(v - Math.round(v)) < 0.005;
  return `${v.toLocaleString("fr-FR", { minimumFractionDigits: entier ? 0 : 2, maximumFractionDigits: entier ? 0 : 2 })} €`;
};

/** Texte du badge solidaire d'une carte, ou null quand il n'y a aucun don. Pur. */
export function badgeDon(listing) {
  if (!listing || isFreeListing(listing)) return null;
  const mode = listing.listing_mode
    || (listing.association_id === "buyer_choice" ? "buyer_choice" : listing.association_id ? "normal" : "no_asso");
  if (mode === "no_asso") return null;
  const pct = listing.donation_percentage;
  const montant = listing.donation_amount;
  const qui = mode === "buyer_choice" || !listing.association_name || listing.association_name === "Association"
    ? "une association" : listing.association_name;
  if (!isBookingListing(listing) && typeof montant === "number" && montant > 0) {
    return { texte: _eurCompact(montant), titre: `${_eurCompact(montant)} reversés à ${qui}` };
  }
  if (!pct) return null;
  return { texte: `${pct}%`, titre: `${pct}% ${isBookingListing(listing) ? "de la transaction" : "du prix"} reversés à ${qui}` };
}

// Pilules de filtre (accueil + recherche) — DERIVE du canonique unique
// (taxonomy/gively-taxonomy.json -> taxonomy.generated.js). Ordre marketing =
// TAXONOMY.pillOrder ; libelle COURT = pillLabel. Editer le canonique puis
// `npm run generate-taxonomy` (ne pas modifier a la main). Coherence garantie
// par check-taxonomy.js.
const PILL_CATEGORIES = TAXONOMY.pillOrder.map((id) => ({
  id,
  name: TAXONOMY.categories[id].pillLabel,
}));

// Grille standard des annonces — MÊME taille de carte PARTOUT (accueil,
// recherche, catégories, asso, favoris…) : 3 par ligne sur mobile, 4 sur
// tablette, 5 sur desktop. Source unique pour garantir l'uniformité et éviter
// toute divergence future. Toute liste de ListingCard doit utiliser cette classe.
export const LISTING_GRID = "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3";

const CategoryPills = ({ selected, onSelect, autoScroll = false }) => {
  const categories = PILL_CATEGORIES;

  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  const posRef = useRef(0);

  // Défilement automatique doux (accueil uniquement, via la prop `autoScroll`).
  // MÊME mécanique que le carrousel « Nos partenaires associatifs » :
  // requestAnimationFrame + scrollLeft, liste dupliquée pour une boucle continue
  // sans saccade, pause au survol/tactile, respect de prefers-reduced-motion.
  // Désactivé par défaut → la page Recherche garde des pills statiques (on ne
  // déplace pas un filtre que l'utilisateur est en train d'utiliser).
  useEffect(() => {
    if (!autoScroll) return undefined;
    const el = trackRef.current;
    if (!el) return undefined;
    const reduce = window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const SPEED = 0.4; // px / frame ≈ 24 px/s, très doux
    let raf;
    const step = () => {
      if (el) {
        if (pausedRef.current) {
          posRef.current = el.scrollLeft; // resynchronise après interaction
        } else {
          const half = el.scrollWidth / 2;
          posRef.current += SPEED;
          if (half > 0 && posRef.current >= half) posRef.current -= half;
          el.scrollLeft = posRef.current;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [autoScroll]);

  const pause = () => { pausedRef.current = true; };
  const resume = () => { pausedRef.current = false; };
  const resumeSoon = () => { setTimeout(() => { pausedRef.current = false; }, 1500); };
  // En auto-défilement, liste dupliquée pour la boucle continue. Le testid
  // canonique ne reste que sur la 1re occurrence (évite les doublons de testid).
  const items = autoScroll ? [...categories, ...categories] : categories;

  return (
    <div
      ref={trackRef}
      className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
      data-testid="category-pills"
      onMouseEnter={autoScroll ? pause : undefined}
      onMouseLeave={autoScroll ? resume : undefined}
      onTouchStart={autoScroll ? pause : undefined}
      onTouchEnd={autoScroll ? resumeSoon : undefined}
    >
      {items.map((cat, i) => (
        <button
          key={`${cat.id}-${i}`}
          onClick={() => onSelect(selected === cat.id ? null : cat.id)}
          className={`category-pill whitespace-nowrap ${selected === cat.id ? 'active' : ''}`}
          data-testid={i < categories.length ? `category-${cat.id}` : undefined}
        >
          {cat.name}
        </button>
      ))}
    </div>
  );
};

// Listing Card with Favorite Button

// React.memo : évite de re-rendre chaque carte quand le parent (grille de
// recherche/accueil) se met à jour alors que les props de la carte sont
// inchangées — gain de fluidité au scroll sur les longues listes (audit perf).
// onUnfavorited(listing_id) : callback optionnel appelé quand l'utilisateur
// RETIRE l'annonce de ses favoris (utilisé par la page Favoris pour faire
// disparaître la carte de la liste). Optionnel → aucun impact ailleurs.
// `prioritaire` : la carte est dans le premier écran d'une liste (Lighthouse du
// 19/10/2026 : sur /search, l'image LCP était une carte en `loading="lazy"`,
// chargée 1,5 s après le reste). Elle se charge alors tout de suite, en priorité.
const ListingCardBase = ({ listing, showFavorite = true, onUnfavorited, prioritaire = false }) => {
  const sponsorise = classementSponsorise(listing);
  const { user, updateUser } = useAuth();
  const { account: assoAccount } = useAssociationAuth() || {};
  const [isFavorited, setIsFavorited] = useState(listing.is_favorited || false);
  const [favLoading, setFavLoading] = useState(false);

  // Suit la liste de favoris du profil DANS LES DEUX SENS (30/09/2026) : avant,
  // on ne faisait qu'allumer le cœur — retiré depuis la fiche, il restait plein
  // sur les autres cartes. Une page qui AFFIRME le favori (`is_favorited:true`,
  // la page Favoris) n'est jamais contredite par un profil pas encore rafraîchi.
  useEffect(() => {
    if (Array.isArray(user?.favorites)) {
      setIsFavorited(listing.is_favorited === true || user.favorites.includes(listing.listing_id));
    }
  }, [user, listing.listing_id, listing.is_favorited]);

  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user && assoAccount) {
      notifyAssoCannot("favorite");
      return;
    }
    if (!user) {
      toast.error("Connectez-vous pour ajouter aux favoris");
      return;
    }

    // UI optimiste (parité Vinted/Leboncoin) : le cœur se remplit INSTANTANÉMENT,
    // on réconcilie ensuite avec le serveur et on revient en arrière si l'appel
    // échoue. Le bouton reste `disabled` pendant la requête (anti double-clic ;
    // le backend favoris est idempotent).
    const next = !isFavorited;
    setIsFavorited(next);
    setFavLoading(true);
    try {
      const res = next
        ? await api.post(`/favorites/${listing.listing_id}`)
        : await api.delete(`/favorites/${listing.listing_id}`);
      toast.success(next ? "Ajouté aux favoris 💚" : "Retiré des favoris");
      if (res.data?.favorites && updateUser) updateUser({ ...user, favorites: res.data.favorites });
      // Retrait confirmé par le serveur → laisser le parent retirer la carte
      // (page Favoris). Après succès uniquement, pas de rollback de liste à gérer.
      if (!next && onUnfavorited) onUnfavorited(listing.listing_id);
    } catch (error) {
      setIsFavorited(!next); // rollback : le serveur n'a pas confirmé
      toast.error("Vos favoris n'ont pas pu être mis à jour. Réessayez.");
    } finally {
      setFavLoading(false);
    }
  };

  return (
    <Link
      to={`/listing/${slugify(listing.title, listing.listing_id)}`}
      className="group block"
      data-testid={`listing-card-${listing.listing_id}`}
    >
      {/* Photo style Leboncoin : 4 coins arrondis, ombre douce, AUCUNE
          bordure (les "petits traits" venaient du border de .listing-card). */}
      <div className="aspect-[4/3] rounded-2xl overflow-hidden relative bg-slate-100 shadow-sm group-hover:shadow-md transition-shadow">
        <img
          src={listing.thumbnails?.[0] || listing.images?.[0] || NO_IMAGE_PLACEHOLDER}
          alt={buildListingImageAlt(listing)}
          loading={prioritaire ? "eager" : "lazy"}
          fetchPriority={prioritaire ? "high" : undefined}
          decoding="async"
          width="400"
          height="300"
          onError={(e) => {
            // Image cassée (URL morte / 404) → placeholder NEUTRE une seule fois,
            // jamais de boucle de rechargement, jamais une fausse photo de produit.
            if (e.currentTarget.src !== NO_IMAGE_PLACEHOLDER) e.currentTarget.src = NO_IMAGE_PLACEHOLDER;
          }}
          className="listing-image w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {/* Annonce vendue : voile + badge VENDU */}
        {listing.status === "sold" && (
          <div className="absolute inset-0 bg-slate-900/45 flex items-center justify-center z-20">
            <span className="bg-white text-slate-900 text-sm font-extrabold px-4 py-1.5 rounded-full shadow-lg -rotate-6 uppercase tracking-wide">
              {getTerms(listing).soldBadge}
            </span>
          </div>
        )}
        {/* Annonce réservée (offre acceptée non payée) : badge Réservé. */}
        {listing.status !== "sold" && isReserved(listing) && (
          <div className="absolute inset-0 bg-slate-900/35 flex items-center justify-center z-20">
            <span className="bg-amber-500 text-white text-sm font-extrabold px-4 py-1.5 rounded-full shadow-lg -rotate-6 uppercase tracking-wide">
              Réservé
            </span>
          </div>
        )}
        {/* Boost badges + Pro badge — décalés SOUS le favori (qui occupe
            top-3 left-3) pour éviter la collision visuelle (audit GIVELY). */}
        {(listing.is_featured || listing.is_urgent || listing.seller_account_type === "professional" || sponsorise) && (
          <div className="absolute top-14 left-3 flex flex-col gap-1 z-10">
            {listing.seller_account_type === "professional" && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-600 text-white font-semibold shadow-sm">
                PRO
              </span>
            )}
            {listing.is_featured && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-600 text-white font-semibold flex items-center gap-1 shadow-sm">
                <StarIcon className="w-3 h-3" filled />
                À la Une
              </span>
            )}
            {listing.is_urgent && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500 text-white font-semibold flex items-center gap-1 shadow-sm">
                <FireIcon className="w-3 h-3" />
                Urgent
              </span>
            )}
            {/* D111-6 II : le classement de ce résultat a été influencé par une
                option payante (utils/classementSponsorise.js). */}
            {sponsorise && (
              <span
                className="text-[11px] px-2 py-0.5 rounded-full bg-white/95 text-slate-700 font-semibold shadow-sm"
                title="Classement influencé par une option de mise en avant payée par le vendeur"
                data-testid="pastille-sponsorise"
              >
                Sponsorisé
              </span>
            )}
          </div>
        )}
        {/* Badge solidaire — présent sur chaque carte vendue (03/11/2026).
            Avant : « ♥ 25% », abstrait. Désormais le MONTANT du don (« ♥ 5 € »),
            calculé par le serveur (``donation_amount``, payment_split) : c'est
            ce qui partira vraiment à l'association. Location/service : le
            prix est par jour/heure, on garde le pourcentage. Annonce gratuite
            ou sans association : aucun don, donc aucun badge (même règle que
            la fiche annonce). */}
        {badgeDon(listing) && (
          <div
            title={badgeDon(listing).titre}
            aria-label={badgeDon(listing).titre}
            data-testid="carte-badge-don"
            className={`absolute top-3 right-3 text-white text-[11px] font-bold px-2 py-0.5 rounded-full shadow-sm ${
            listing.seller_type === "association" || listing.donation_percentage === 100
              ? "bg-purple-600"
              : "bg-[#1DA1F2]"
          }`}>
            ♥ {badgeDon(listing).texte}
          </div>
        )}
        {showFavorite && (
          <button
            onClick={toggleFavorite}
            disabled={favLoading}
            className={`absolute top-3 left-3 p-2 rounded-full shadow-md transition-all active:scale-90 ${
              isFavorited
                ? 'bg-green-50 text-emerald-500'
                : 'bg-white/90 text-slate-400 hover:text-emerald-500'
            }`}
            data-testid={`favorite-btn-${listing.listing_id}`}
            aria-label={isFavorited ? "Retirer des favoris" : "Ajouter aux favoris"}
            aria-pressed={isFavorited}
          >
            <HeartIcon className="w-5 h-5" filled={isFavorited} aria-hidden="true" />
          </button>
        )}
      </div>
      
      <div className="pt-2 px-0.5 space-y-0.5">
        <h3 className="font-semibold text-slate-900 line-clamp-1 leading-snug">{listing.title}</h3>
        <p className={`text-base font-bold leading-tight flex items-center gap-1.5 ${isFreeListing(listing) ? "text-emerald-600" : "text-[#1DA1F2]"}`}>
          {formatListingPrice(listing)}
          {/* Badge type (location/service) uniquement — un free n'a pas de badge,
              son prix affiche déjà « Gratuit » (cf. bookingBadge / formatListingPrice). */}
          {bookingBadge(listing) && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-teal-100 text-teal-700">{bookingBadge(listing).label}</span>
          )}
        </p>
        <p className="text-xs text-slate-500 leading-tight">{listing.city}</p>

        <div className="flex items-center gap-1.5">
          {listing.association_logo && (
            <img
              src={listing.association_logo}
              alt={listing.association_name}
              loading="lazy"
              decoding="async"
              width="20"
              height="20"
              className="w-4 h-4 rounded-full object-cover shrink-0"
            />
          )}
          <span className="text-xs text-slate-600 line-clamp-1">{listing.association_name}</span>
        </div>
        {/* Note du vendeur retirée des cartes d'annonce (accueil, recherche…) :
            les étoiles restent visibles sur le profil vendeur et la fiche annonce. */}
      </div>
    </Link>
  );
};

// Suggest Association Card - Viral Loop Component

const SuggestAssociationCard = ({ searchTerm }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: searchTerm || "",
    city: "",
    website: "",
    description: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Connectez-vous pour suggérer une association");
      navigate("/login");
      return;
    }
    
    if (!formData.name || !formData.city) {
      toast.error("Le nom et la ville sont requis");
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post("/associations/suggest", formData);
      if (response.data.status === "already_suggested") {
        toast.success("Cette association a déjà été suggérée ! Votre intérêt a été comptabilisé.");
      } else {
        toast.success("Merci ! Nous allons contacter cette association pour l'inviter à rejoindre Gively.");
      }
      setShowForm(false);
      setFormData({ name: "", city: "", website: "", description: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="text-center py-12 max-w-lg mx-auto">
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-100">
        <div className="w-16 h-16 bg-[#1DA1F2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <HeartIcon className="w-8 h-8 text-[#1DA1F2]" />
        </div>
        
        <h3 className="text-xl font-bold text-slate-900 mb-2">
          Association introuvable ?
        </h3>
        <p className="text-slate-600 mb-6">
          L'association "<span className="font-semibold text-[#1DA1F2]">{searchTerm}</span>" n'est pas encore sur Gively. 
          Aidez-nous à l'inviter !
        </p>
        
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="bg-[#1DA1F2] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#1a8cd8] transition-colors"
            data-testid="suggest-association-btn"
          >
            Suggérer cette association
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="text-left space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom de l'association *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#1DA1F2] focus:border-transparent"
                placeholder="Ex: Les Restos du Cœur"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ville *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#1DA1F2] focus:border-transparent"
                placeholder="Ex: Toulouse"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Site web (optionnel)</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#1DA1F2] focus:border-transparent"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Pourquoi cette association ? (optionnel)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#1DA1F2] focus:border-transparent resize-none"
                rows={2}
                placeholder="Ex: C'est mon association locale préférée..."
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-[#1DA1F2] text-white font-semibold rounded-xl hover:bg-[#1a8cd8] transition-colors disabled:opacity-50"
                data-testid="submit-suggestion-btn"
              >
                {loading ? "Envoi..." : "Envoyer"}
              </button>
            </div>
          </form>
        )}
        
        <p className="text-xs text-slate-400 mt-4">
          Nous contacterons l'association pour l'inviter à rejoindre la plateforme.
        </p>
      </div>
    </div>
  );
};

// Association Card

const ListingCardSkeleton = () => (
  <div className="animate-pulse">
    <div className="aspect-[4/3] rounded-2xl bg-slate-200" />
    <div className="pt-2 px-0.5 space-y-1.5">
      <div className="h-4 bg-slate-200 rounded w-3/4" />
      <div className="h-4 bg-slate-200 rounded w-1/4" />
      <div className="h-3 bg-slate-200 rounded w-1/2" />
    </div>
  </div>
);


// Grille de squelettes — réutilise le SEUL squelette fidèle à la carte réelle
// (ListingCardSkeleton : aspect-[4/3], sans bordure). L'ancien <ListingSkeleton>
// (cadre h-48 bordé blanc) ne correspondait PAS à la carte rendue → flash/CLS au
// chargement ; il a été supprimé (1 implémentation officielle, anti-doublon).
const ListingsSkeletonGrid = ({ count = 8 }) => (
  <div className={LISTING_GRID}>
    {Array.from({ length: count }).map((_, i) => (
      <ListingCardSkeleton key={i} />
    ))}
  </div>
);

const ListingCard = React.memo(ListingCardBase);

export { CategoryPills, ListingCard, SuggestAssociationCard, ListingCardSkeleton, ListingsSkeletonGrid };
